import { useCallback, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import { QUERY_SETTINGS } from "api/landlord/landlord";
import { getErrorMessage } from "utils/error";
import { toast } from "react-toastify";
import { useAuthContext } from "providers/Auth";
import { ApplicationStatus } from "shared/types";
import { ApplicationActions } from "shared/utils/application-actions";
import {
  Applicant,
  Application,
  MakeDecisionRequirements
} from "types/application";
import { UndoDecisionModal as UndoDecisionModalBase } from "./UndoDecisionModal";

interface UseDecisionButtonParams {
  applicationId: string;
  status: ApplicationStatus;
  applicants?: Applicant[];
  application?: Application;
  requirements?: MakeDecisionRequirements[];
  decisionAt: string | null;
}

/**
 * Owns the entire "Undo decision" flow shared between the group-application
 * and the applicant report headers: the settings fetch that derives
 * `undoDecisionDays`, the `undo_decision` mutation, the confirmation modal, and
 * the `ApplicationActions` gating (built with `decisionAt` + `undoDecisionDays`
 * so the enable/disable/tooltip states are correct).
 */
export const useDecisionButton = ({
  applicationId,
  status,
  applicants,
  application,
  requirements,
  decisionAt
}: UseDecisionButtonParams) => {
  const { permissions } = useAuthContext();
  const queryClient = useQueryClient();

  const [undoDecisionModalOpened, setUndoDecisionModalOpened] = useState(false);
  const [notifyApplicant, setNotifyApplicant] = useState(false);

  const { data: undoDecisionDays } = useQuery({
    queryKey: [QUERY_SETTINGS],
    queryFn: () => instance.get("/portal/v1/settings"),
    select: (response) => {
      const applicationSettings = response.data.settings?.find(
        (setting: { name: string }) => setting.name === "application_settings"
      );
      const undoDecision = applicationSettings?.value?.undo_decision;
      if (!undoDecision || undoDecision.active === false) return null;
      return undoDecision.days ?? 30;
    }
  });

  const appActions = new ApplicationActions({
    status,
    permissions,
    applicants,
    application,
    decisionAt,
    requirements,
    undoDecisionDays: undoDecisionDays !== undefined ? undoDecisionDays : 30
  });

  const { mutate: undoDecision, isPending: undoDecisionPending } = useMutation({
    mutationFn: () => {
      return instance.post(
        `portal/v1/applications/${applicationId}/undo_decision`,
        {
          notify_applicant: notifyApplicant
        }
      );
    },
    onSuccess: () => {
      toast.success("Application successfully updated");
      queryClient.invalidateQueries({
        queryKey: ["APPLICATION", applicationId]
      });
      queryClient.invalidateQueries({
        queryKey: ["APPLICANTS_TABLE"]
      });
      setUndoDecisionModalOpened(false);
    },
    onError: (e) => {
      toast.error(getErrorMessage(e));
    }
  });

  const UndoDecisionModal = useCallback(
    () => (
      <UndoDecisionModalBase
        opened={undoDecisionModalOpened}
        onClose={() => {
          setUndoDecisionModalOpened(false);
        }}
        undoDecisionPending={undoDecisionPending}
        onUndo={() => undoDecision()}
        setNotifyApplicant={setNotifyApplicant}
        notifyApplicant={notifyApplicant}
      />
    ),
    [
      undoDecisionModalOpened,
      undoDecisionPending,
      notifyApplicant,
      undoDecision
    ]
  );

  return {
    appActions,
    openUndoModal: () => setUndoDecisionModalOpened(true),
    UndoDecisionModal
  };
};
